'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar as CalendarIcon, Copy, RefreshCw, Check, CalendarDays } from 'lucide-react';
import { format, addYears, isBefore, isAfter, parseISO, isValid } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomDateGeneratorContent } from '../../data/random-date-generator-content';
import Link from 'next/link';

type DateFormat = 'yyyy-MM-dd' | 'MM/dd/yyyy' | 'dd/MM/yyyy' | 'MMMM d, yyyy' | 'PPP';

export default function RandomDateGenerator() {
    const [startDate, setStartDate] = useState<Date>(() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 10);
        return date;
    });

    const [endDate, setEndDate] = useState<Date>(() => new Date());
    const [result, setResult] = useState<string>('');
    const [generatedDate, setGeneratedDate] = useState<Date | null>(null);
    const [copied, setCopied] = useState<boolean>(false);
    const [dateFormat, setDateFormat] = useState<DateFormat>('yyyy-MM-dd');
    const [showStartCalendar, setShowStartCalendar] = useState<boolean>(false);
    const [showEndCalendar, setShowEndCalendar] = useState<boolean>(false);
    const { toast } = useToast();

    const formatOptions: { value: DateFormat; label: string }[] = [
        { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD' },
        { value: 'MM/dd/yyyy', label: 'MM/DD/YYYY' },
        { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY' },
        { value: 'MMMM d, yyyy', label: 'Month D, YYYY' },
        { value: 'PPP', label: 'Formal (e.g., Jan 1, 2023)' },
    ];

    const generateRandomDate = useCallback(() => {
        try {
            // Ensure dates are valid
            if (!isValid(startDate) || !isValid(endDate)) {
                throw new Error('Invalid date range');
            }

            // Ensure start date is before end date
            const start = isBefore(startDate, endDate) ? startDate : endDate;
            const end = isAfter(endDate, startDate) ? endDate : startDate;

            // Calculate time difference in milliseconds
            const timeDiff = end.getTime() - start.getTime();

            // Generate random time within the range
            const randomTime = start.getTime() + Math.random() * timeDiff;
            const randomDate = new Date(randomTime);

            // Store the actual date object for consistent formatting
            setGeneratedDate(randomDate);

            // Format the date based on selected format
            let formattedDate: string;

            if (dateFormat === 'PPP') {
                // Use date-fns format for more natural language formatting
                formattedDate = format(randomDate, 'PPP');
            } else {
                formattedDate = format(randomDate, dateFormat);
            }

            setResult(formattedDate);
        } catch (error) {
            console.error('Error generating date:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate date. Please check your date range.',
                variant: 'destructive',
            });
        }
    }, [startDate, endDate, dateFormat, toast]);

    const handleCopy = useCallback(() => {
        if (!result) return;

        navigator.clipboard.writeText(result);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Date has been copied to clipboard.',
        });

        setTimeout(() => setCopied(false), 2000);
    }, [result, toast]);

    const handleDateInputChange = (type: 'start' | 'end', value: string) => {
        const date = parseISO(value);
        if (isValid(date)) {
            if (type === 'start') {
                setStartDate(date);
            } else {
                setEndDate(date);
            }
        }
    };

    // Generate initial date on mount
    useEffect(() => {
        generateRandomDate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="mb-8 text-center">
                    <ToolPageIntro title="Random Date Generator">
                Generate random dates with our free online tool. Create timestamps, date ranges, and temporal data for testing, simulations, and development.
            </ToolPageIntro>

                    <div className="text-sm text-muted-foreground">
                        {generatedDate ? format(generatedDate, 'EEEE, MMMM d, yyyy') : 'No date generated'}
                    </div>
                </div>

                <div className="grid gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Date Range</CardTitle>
                            <CardDescription>Set the start and end dates for random selection</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Start Date</Label>
                                    <Popover open={showStartCalendar} onOpenChange={setShowStartCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className={cn(
                                                    "w-full justify-start text-left font-normal",
                                                    !startDate && "text-muted-foreground"
                                                )}
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {startDate ? format(startDate, 'PPP') : <span>Pick a date</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={startDate}
                                                onSelect={(date) => {
                                                    if (date) {
                                                        setStartDate(date);
                                                        setShowStartCalendar(false);
                                                    }
                                                }}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    <Input
                                        type="date"
                                        value={format(startDate, 'yyyy-MM-dd')}
                                        onChange={(e) => handleDateInputChange('start', e.target.value)}
                                        className="mt-2"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>End Date</Label>
                                    <Popover open={showEndCalendar} onOpenChange={setShowEndCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className={cn(
                                                    "w-full justify-start text-left font-normal",
                                                    !endDate && "text-muted-foreground"
                                                )}
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {endDate ? format(endDate, 'PPP') : <span>Pick a date</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={endDate}
                                                onSelect={(date) => {
                                                    if (date) {
                                                        setEndDate(date);
                                                        setShowEndCalendar(false);
                                                    }
                                                }}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    <Input
                                        type="date"
                                        value={format(endDate, 'yyyy-MM-dd')}
                                        onChange={(e) => handleDateInputChange('end', e.target.value)}
                                        className="mt-2"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Date Format</Label>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                    {formatOptions.map((option) => (
                                        <Button
                                            key={option.value}
                                            variant={dateFormat === option.value ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setDateFormat(option.value)}
                                            className="text-xs"
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <div className="pt-2">
                                <Button
                                    onClick={generateRandomDate}
                                    className="w-full"
                                >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Generate Random Date
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex justify-between items-center">
                                <CardTitle>Generated Date</CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!result}
                                >
                                    {copied ? (
                                        <Check className="h-4 w-4 mr-2" />
                                    ) : (
                                        <Copy className="h-4 w-4 mr-2" />
                                    )}
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {result ? (
                                <div
                                    className="bg-muted/50 p-6 rounded-md text-center cursor-pointer hover:bg-muted/70 transition-colors"
                                    onClick={handleCopy}
                                >
                                    <div className="text-3xl font-bold mb-2">
                                        {result}
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                        {generatedDate ? format(generatedDate, 'EEEE, MMMM d, yyyy') : 'No date generated'}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <CalendarDays className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                    <p>Click "Generate" to create a random date</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Quick Presets</CardTitle>
                            <CardDescription>Generate dates from common ranges</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        const today = new Date();
                                        setStartDate(today);
                                        setEndDate(addYears(today, 1));
                                    }}
                                >
                                    Next 12 Months
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        const today = new Date();
                                        setStartDate(addYears(today, -1));
                                        setEndDate(today);
                                    }}
                                >
                                    Past 12 Months
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        const today = new Date();
                                        setStartDate(new Date(today.getFullYear(), 0, 1)); // Start of year
                                        setEndDate(new Date(today.getFullYear(), 11, 31)); // End of year
                                    }}
                                >
                                    This Year
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        const today = new Date();
                                        setStartDate(new Date(today.getFullYear() - 100, 0, 1)); // 100 years ago
                                        setEndDate(today);
                                    }}
                                >
                                    Last 100 Years
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>
                        This tool generates random dates within your specified range.
                        All processing happens in your browser - no data is sent to any server.
                    </p>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomDateGeneratorContent.title}
                description={randomDateGeneratorContent.description}
                content={randomDateGeneratorContent.content}
                faqs={randomDateGeneratorContent.faqs}
                relatedTools={randomDateGeneratorContent.relatedTools}
            />
        </div>
    );
}
